import { spaceGroupApi } from "@circle-react/api/spaceGroupApi";
import { useSpaceGroupsContext } from "@circle-react/contexts/spaceGroupsContext";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { useIsCircleV4FeatureEnabled } from "@circle-react/hooks/useIsCircleV4FeatureEnabled";
import { useLinkToRoot } from "@circle-react/hooks/useLinkToRoot";
import { spaceInvalidations } from "@circle-react/lib/queries/spaces";
import isFunction from "lodash/isFunction";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { SecureConfirmationModal } from "@/react/components/SettingsApp/CompassCompat/SecureConfirmationModal";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import { CONTENT_HARD_DELETION_DELAY_KEY, Details } from "./Details";
import { DetailsComponent } from "./DetailsComponent";

export interface DeleteSpaceGroupModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  show: boolean;
  spaceGroupId: number;
}

export const DeleteSpaceGroupModal = ({
  show,
  spaceGroupId,
  onClose,
  onSuccess,
}: DeleteSpaceGroupModalProps) => {
  const isCircleV4 = useIsCircleV4FeatureEnabled("circle_v4_modals_enabled");
  const { error, success } = useToast();
  const { goToCommunityRootPath } = useLinkToRoot();

  const {
    records: spaces,
    actions: { refresh: refreshSpaces },
  } = useSpacesContext();
  const {
    actions: { refresh: refreshSpaceGroups },
  } = useSpaceGroupsContext();

  const { mutate: deleteSpaceGroupMutation, isPending: isDeleting } =
    useMutation({
      mutationFn: () => spaceGroupApi.destroy(spaceGroupId),
      onSuccess: async () => {
        success(t("space_groups.form.delete.success"));
        // eslint-disable-next-line @typescript-eslint/await-thenable -- Promise.all works even with non-promise values
        await Promise.all([refreshSpaces(), refreshSpaceGroups()]);
        await spaceInvalidations.all();
        goToCommunityRootPath();
        if (isFunction(onClose)) {
          onClose();
        }

        if (isFunction(onSuccess)) {
          onSuccess();
        }
      },
      onError: () => {
        error(t("space_groups.form.delete.failed"));
      },
    });

  const spacesInSpaceGroup = spaces.filter(
    space => space.space_group_id === spaceGroupId,
  );

  const modalProps = {
    title: t("space_groups.delete.title"),
    isOpen: show,
    confirmUserPrompt: t("space_groups.delete.confirm_prompt"),
    onClose,
    onCancel: onClose,
    onConfirm: deleteSpaceGroupMutation,
    confirmVariant: "danger" as const,
    isDisabled: isDeleting,
    confirmText: isDeleting ? t("deleting") : t("confirm"),
  };

  const description = t("space_groups.delete.description", {
    content_hard_deletion_delay: t(CONTENT_HARD_DELETION_DELAY_KEY),
  });

  if (isCircleV4) {
    return (
      <SecureConfirmationModal
        {...modalProps}
        description={description}
        position="top"
        promptGrouping="inline"
        size="lg"
      >
        <DetailsComponent spaces={spacesInSpaceGroup} />
      </SecureConfirmationModal>
    );
  }

  const v3Description = [
    description,
    spacesInSpaceGroup.length > 0 && t("space_groups.delete.this_includes"),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <SecureConfirmationModal {...modalProps}>
      <Details
        description={v3Description}
        spaces={spacesInSpaceGroup}
        spaceGroupId={spaceGroupId}
      />
    </SecureConfirmationModal>
  );
};
